/**
 * Bot think pacing: base delay (ms) and multiplier ranges for random think duration.
 * Total think time is randomInt(multMin, multMax) * baseDelayMs — see sampleBotThinkDelayMs.
 */

import { z } from 'zod';

import { randomInt } from './utilRandom';

export type PacingEvent =
  | 'mergeCards'
  | 'cardSelect'
  | 'moveSelect'
  | 'dicePickVisibility'
  | 'diceResultDisplay';

const pacingEventSchema = z.enum([
  'mergeCards',
  'cardSelect',
  'moveSelect',
  'dicePickVisibility',
  'diceResultDisplay',
]);

const rangeSchema = z
  .object({
    min: z.number().int().nonnegative(),
    max: z.number().int().nonnegative(),
  })
  .refine((r) => r.min <= r.max, { message: 'min must be <= max' });

const botPacingProfileSchema = z.object({
  baseDelayMs: z.number().int().nonnegative(),
  ranges: z.record(pacingEventSchema, rangeSchema),
});

export type BotPacingProfile = z.infer<typeof botPacingProfileSchema>;

function defineProfile(profile: BotPacingProfile): BotPacingProfile {
  const result = botPacingProfileSchema.safeParse(profile);
  if (!result.success) {
    throw new Error(`Invalid bot pacing profile:\n${result.error.toString()}`);
  }
  return result.data;
}

export const BOT_PACING_LOCAL_TRAINING: BotPacingProfile = {
  baseDelayMs: 150,
  ranges: {
    mergeCards: { min: 4, max: 6 },
    cardSelect: { min: 10, max: 18 },
    moveSelect: { min: 12, max: 20 },
    /** Think before committing dice pick; must exceed client shuffle (3s) so values are visible */
    dicePickVisibility: { min: 25, max: 35 },
    /** Pause after dice pick (SELECTDICESOPP) before next state so players see the result */
    diceResultDisplay: { min: 10, max: 15 },
  },
};

export const BOT_PACING_REMOTE: BotPacingProfile = {
  baseDelayMs: 200,
  ranges: {
    ...BOT_PACING_LOCAL_TRAINING.ranges,
  },
};

defineProfile(BOT_PACING_LOCAL_TRAINING);
defineProfile(BOT_PACING_REMOTE);

/** Local training / offline ControllerLocalWrapper — playtest to match perceived remote pace. */
export const BOT_DELAY_MS_LOCAL_TRAINING = BOT_PACING_LOCAL_TRAINING.baseDelayMs;

/** Server-hosted games (newgame, tournaments, fillGameWithBots, restore, replace-with-bot). */
export const BOT_DELAY_MS_REMOTE = BOT_PACING_REMOTE.baseDelayMs;

export type BotThinkTier =
  | 'short' // legacy: maps to cardSelect (typical "think before acting")
  | 'medium' // legacy: maps to moveSelect (slightly longer; kept for compatibility)
  | 'mergeCards'
  | 'cardSelect'
  | 'moveSelect'
  | 'dicePickVisibility'
  | 'diceResultDisplay';

/**
 * Random think delay in ms for one controller message, derived from profile base delay and tier.
 */
export function sampleBotThinkDelayMs(
  profile: BotPacingProfile,
  tier: BotThinkTier = 'short',
): number {
  const event: PacingEvent =
    tier === 'mergeCards'
      ? 'mergeCards'
      : tier === 'dicePickVisibility'
        ? 'dicePickVisibility'
        : tier === 'diceResultDisplay'
          ? 'diceResultDisplay'
          : tier === 'moveSelect' || tier === 'medium'
            ? 'moveSelect'
            : 'cardSelect';

  const { min, max } = profile.ranges[event];
  const mult = randomInt(min, max);
  return mult * profile.baseDelayMs;
}

/**
 * Build a pacing profile when only a legacy base delay (ms) is known (e.g. simulations with delay 0).
 * Uses shared multiplier ranges from remote profile.
 */
export function pacingProfileFromBaseDelayMs(baseDelayMs: number): BotPacingProfile {
  return {
    baseDelayMs,
    ranges: BOT_PACING_REMOTE.ranges,
  };
}
